import {
  AbstractControl,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { TypedDictionary } from '@core/utils';
import { RecentAirport } from '@shared/types';
import { showValidation } from '@shared/ui/utils/form-control-utils/show-validation';
import { codesToAirports } from '@store/origins/api';
import { Airport } from '@store/origins/types';
import { containsWordAndPunctuation } from 'jb-component-library';
import moize from 'moize';
import {
  concat,
  contains,
  filter,
  pluck,
  propEq,
  replace,
  test,
  toUpper,
  uniq,
  values,
} from 'ramda';

import { CitySelectorType } from '../types/city-selector-type.enum';

export function airportInvalidCodeValidator(message: string): ValidatorFn {
  return ({ value }: AbstractControl): ValidationErrors => {
    return value && value.code ? null : { cityInvalid: message };
  };
}

/**
 * Function to check if any of the words of string1 start with string2
 * @param {string} string1 a string that is delimited by
 * comma OR space OR slash OR right parenth OR left parenth
 * @param {string} string2 the string to be searched
 * @return {boolean} true if words in string1 start with string2, false otherwise
 */

export const startsWithStringIgnoreCase = moize(
  (string1: string, string2: string): boolean => {
    const words = string1.split(/[,|\s|\/|)|(]+/);
    let match = false;

    words.forEach(word => {
      if (word.toLowerCase().indexOf(string2.toLowerCase()) === 0) {
        match = true;
      }
    });

    return match;
  },
);

/**
 * NOTE TO DEVELOPER: non-optimized filter
 * Function to search airports with a needle provided.
 * @param {Airport[]} airports a list of airports
 * @param {string} input user input. Could be one word or space and comma delimited words
 * @return {Airport[]} a list of airports that either name or code contains
 * input. If needle is not passed in, it would return empty list.
 * If user entered 3 letter only, it would assume that user entered a airport
 * code and put the matching airports with airport code on the top.
 * As long as the airport name contains the input, it would return it.
 */
export const filterAirportsByStartOfWord = moize(
  (airports: Airport[], input?: string): Airport[] => {
    if (!input) {
      return [];
    }
    let filteredAirports = [];
    const breakInput = input.trim().split(/[,|\s|\/|)|(]+/);

    // Check if any airport code matches when exactly 3 characters are entered
    if (test(/^[A-Z]{3}$/i, input)) {
      const transformedInput = toUpper(input);
      filteredAirports = filteredAirports.concat(
        filter(propEq('code', transformedInput))(airports),
      );
    }

    filteredAirports = filteredAirports.concat(
      filter((airport: Airport) => {
        // Check that all the words in the input are part of the needle
        let howManyInputs = breakInput.length;
        const extendedNeedle = `${airport.needle} ${airport.name}`;

        breakInput.forEach(inputWord => {
          if (startsWithStringIgnoreCase(extendedNeedle, inputWord)) {
            howManyInputs--;
          }
        });

        return !contains(airport, filteredAirports) && howManyInputs === 0;
      })(airports),
    );

    return filteredAirports;
  },
);

export const generateNoMatchText = moize(
  (search: string, message: string): string => {
    return replace('[SEARCHSTRING]', search, message.replace(/[/\\*]/g, ''));
  },
);

export function airportValidator(message: string): ValidatorFn {
  return (control: AbstractControl): { [key: string]: string } => {
    const value = control.value;

    if (!value || !value.code) {
      return { cityInvalid: message };
    }
    return null;
  };
}

export function setCitySelctorValidation(
  control: AbstractControl | UntypedFormControl,
  message: string,
): void {
  control.setValidators(airportValidator(message));
  showValidation(control);
}

/**
 * Function to search airports with a needle provided.
 * @param {Airport[]} airports a list of airports
 * @param {string} input user capitalized input.
 * returns a list of airports, each airport containing the input in its needle.
 */
const filterAirportsByNeedle = (
  airports: Airport[],
  transformedInput: string,
): Airport[] => {
  const filteredAirportsByNeedle = filter((airport: Airport) => {
    const extendedNeedle = toUpper(`${airport.needle} ${airport.name}`);
    return extendedNeedle.includes(transformedInput);
  }, airports);
  return filteredAirportsByNeedle;
};

export const filterAirportsUtility = (
  airports: Airport[],
  airportHashMap: TypedDictionary<Airport>,
  input?: string,
): Airport[] => {
  if (!input) {
    return [];
  }
  const transformedInput = toUpper(input);
  let airportByCode: Airport;

  // filter by code
  if (/^[A-Z]{3}$/i.test(input)) {
    airportByCode = airportHashMap[transformedInput];
  }

  // filter by needle
  const airportsByNeedle: Airport[] = filterAirportsByNeedle(
    airports,
    transformedInput,
  );

  if (!!airportByCode) {
    return uniq(concat([airportByCode], airportsByNeedle));
  } else {
    return airportsByNeedle;
  }
};

/**
 * Function to check whether string1 contains string2.
 * @param {string} string1 the container string
 * @param {string} string2 the string to be searched
 * @return {boolean} true if string1 contains string2, false otherwise
 */
export const containsStringIngoreCase = moize(
  (string1: string, string2: string): boolean => {
    return string1 && toUpper(string1).includes(toUpper(string2));
  },
);

export function findBlurMatch(
  airportHashMap: TypedDictionary<Airport>,
  input: string,
): Airport {
  let airportMatch: any;
  if (input) {
    const upperInput = toUpper(input);
    // search for code match
    airportMatch = airportHashMap[upperInput];
    // if empty, search for name match
    if (!airportMatch) {
      airportMatch = values(airportHashMap).filter(elem => {
        return toUpper(elem.name).indexOf(upperInput) !== -1;
      })[0];
    }
  }
  return airportMatch || null;
}

export function extractRecentSearchAirports(
  airportsHashMap: TypedDictionary<Airport>,
  searches: RecentAirport[],
  citySelectorType: string,
): Airport[] {
  let recentAirports = [];
  if (!!searches && !!searches.length) {
    // pluck fromCode or toCode from array of recent searches depending on city selector type
    const prop: string =
      citySelectorType === CitySelectorType.ORIGIN
        ? 'originCode'
        : 'destinationCode';
    recentAirports = uniq(pluck(prop)(searches as any));
    // map the array of airport codes to actual airports
    // remove any recent searches that don't exist in supplied hashMap
    recentAirports = codesToAirports(airportsHashMap, recentAirports);
  }
  return recentAirports;
}

export function addShortNameToAirport(airport: Airport): Airport {
  if (airport) {
    let shortName;
    if (airport.cc && airport.cc?.length) {
      shortName = airport.name.replace(/[^area]*$/, '');
    } else if (airport.pm) {
      shortName = airport.name.replace(/\,[^]*\(/, ' (');
    } else if (/\,/.test(airport.name)) {
      shortName = airport.name.replace(/\,[\s\S]*$/, '');
    } else shortName = airport.name.replace(/\s\([^]*$/, '');
    return {
      ...airport,
      shortName,
    };
  }
}

export function addAirportToAllSiblingsNeedleInMac(
  airports: Airport[],
): Airport[] {
  const airportsGroupedByMAC = airports
    .filter(airport => !!airport.pm)
    .reduce((groupedByMac, nextAirport) => {
      return {
        ...groupedByMac,
        [nextAirport.pm]: [
          ...(groupedByMac[nextAirport.pm] || []),
          nextAirport,
        ],
      };
    }, {});

  return airports.map(airport =>
    airport.pm
      ? {
          ...airport,
          needle: `${airport.needle}, ${airportsGroupedByMAC[airport.pm]
            .map(siblingAirport => siblingAirport.name)
            .join(', ')}, ${airportsGroupedByMAC[airport.pm]
            .map(siblingAirport => siblingAirport.name.replace(/,.+\(/, ' ('))
            .join(', ')}`,
        }
      : airport,
  );
}

/** Filter the airports based on a query string. Taken from CL jb-autocomplete.utils.ts */
export function getFilteredCityValues(
  options: Airport[],
  query: string,
): Airport[] {
  const filteredValues = [];
  const numOptions = options.length;
  let optionNumber = 0;
  while (optionNumber < numOptions) {
    const option = options[optionNumber++];
    let matched =
      containsWordAndPunctuation(query)(option.name) ||
      containsWordAndPunctuation(query)(option.needle);

    const areaLength = option.cc ? option.cc.length : 0;
    // The area cities list may contain cities that aren't in the data, so we
    // need to double check against the code as we traverse through the subsequent
    // cities
    const areaCode = option.code;
    if (areaLength) {
      // If it's an area, check the parent area and area cities for a match
      let areaIndex = 0;
      while (
        !matched &&
        areaIndex < areaLength &&
        optionNumber + areaIndex < numOptions
      ) {
        const areaOption = options[optionNumber + areaIndex++];
        matched =
          areaCode === areaOption.pm &&
          (containsWordAndPunctuation(query)(areaOption.name) ||
            containsWordAndPunctuation(query)(areaOption.needle));
      }
      if (matched) {
        filteredValues.push(option);
        for (
          areaIndex = 0;
          areaIndex < areaLength && optionNumber < numOptions;
          areaIndex++
        ) {
          // Check to make sure that the area city is found, since some may be missing
          if (areaCode === options[optionNumber].pm) {
            filteredValues.push(options[optionNumber++]);
          } else {
            break;
          }
        }
      }
    } else if (matched) {
      filteredValues.push(option);
    }
  }
  return filteredValues;
}
